import { useReducer, useCallback, useEffect, useRef } from 'react';
import type { DataState } from '../../types/dataManager';

/**
 * Configuration for a useDomainFetch instance.
 * Each domain context creates one instance per sub-domain.
 */
export interface DomainFetchConfig<T> {
  /** API endpoint path (relative or absolute URL). Pass null to skip fetching entirely. */
  endpoint: string | null;
  /** Optional query parameters appended to the endpoint */
  queryParams?: Record<string, string>;
  /**
   * Auto-refresh interval in milliseconds.
   * 0 or omitted means no automatic refresh.
   */
  refreshInterval?: number;
  /** Returns the current auth token (or null if unauthenticated) */
  getToken: () => string | null;
  /**
   * Optional callback when a 401 response is received.
   * Receives a makeRequest function to retry after token refresh.
   * If not provided, 401 responses dispatch FETCH_ERROR.
   */
  onUnauthorized?: (makeRequest: () => Promise<Response>) => Promise<Response>;
  /** Called with the parsed response data on successful fetch */
  onSuccess?: (data: T) => void;
}

type DomainAction<T> =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; data: T; queryParams?: Record<string, string> }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'MARK_STALE' }
  | { type: 'RESET_BACKOFF' };

function createInitialState<T>(): DataState<T> {
  return {
    data: null,
    loading: false,
    error: null,
    lastFetch: null,
    lastQueryParams: null,
    isStale: true,
    consecutiveFailures: 0,
    lastFailureTime: null,
    backoffUntil: null,
  };
}

// Generic reducer -- TypeScript requires a named function for generic reducers
// to correctly infer the type parameter.
function domainReducer<T>(state: DataState<T>, action: DomainAction<T>): DataState<T> {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        data: action.data,
        loading: false,
        error: null,
        lastFetch: Date.now(),
        lastQueryParams: action.queryParams ?? null,
        isStale: false,
        consecutiveFailures: 0,
        lastFailureTime: null,
        backoffUntil: null,
      };

    case 'FETCH_ERROR': {
      const consecutiveFailures = state.consecutiveFailures + 1;
      const now = Date.now();
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped)
      // Matches DataManagerContext.tsx line 166 exactly.
      const backoffMs = Math.min(30000, Math.pow(2, consecutiveFailures - 1) * 1000);
      return {
        ...state,
        loading: false,
        error: action.error,
        consecutiveFailures,
        lastFailureTime: now,
        backoffUntil: now + backoffMs,
      };
    }

    case 'MARK_STALE':
      return { ...state, isStale: true };

    case 'RESET_BACKOFF':
      return {
        ...state,
        consecutiveFailures: 0,
        lastFailureTime: null,
        backoffUntil: null,
      };

    default:
      return state;
  }
}

export interface UseDomainFetchResult<T> {
  state: DataState<T>;
  /** Trigger a fetch. Pass force=true to bypass backoff. Optional queryParams override the config's queryParams for this request only. */
  refetch: (force?: boolean, queryParams?: Record<string, string>) => Promise<void>;
  dispatch: React.Dispatch<DomainAction<T>>;
}

/**
 * Shared fetch hook used by all 6 domain contexts.
 *
 * Encapsulates:
 * - Exponential backoff on consecutive failures
 * - Staleness tracking
 * - 401 auto-refresh via onUnauthorized callback
 * - Optional auto-refresh interval
 *
 * Note: Uses window.fetch to avoid naming collision with the returned refetch
 * function and React's internal fetch reference.
 */
export function useDomainFetch<T>(config: DomainFetchConfig<T>): UseDomainFetchResult<T> {
  // useReducer with a factory-wrapped initial state to avoid re-creating on every render
  const [state, dispatch] = useReducer(
    (s: DataState<T>, a: DomainAction<T>) => domainReducer<T>(s, a),
    undefined,
    createInitialState<T>,
  );

  // Keep a ref to the current state so the refetch callback can read it
  // without becoming stale after each dispatch.
  const stateRef = useRef<DataState<T>>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Keep a ref to the config so refetch doesn't need to close over it
  // (avoids stale closures when queryParams change).
  const configRef = useRef<DomainFetchConfig<T>>(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const refetch = useCallback(async (force = false, overrideQueryParams?: Record<string, string>): Promise<void> => {
    const currentState = stateRef.current;
    const currentConfig = configRef.current;

    // Skip entirely when endpoint is null (feature disabled)
    if (!currentConfig.endpoint) {
      return;
    }

    // Skip if currently in backoff period and not forced
    if (!force && currentState.backoffUntil && Date.now() < currentState.backoffUntil) {
      return;
    }

    const token = currentConfig.getToken();
    if (!token) {
      // No auth token available -- skip silently
      return;
    }

    dispatch({ type: 'FETCH_START' });

    // Use override params if provided, then last successful queryParams, then config defaults
    const effectiveQueryParams = overrideQueryParams ?? currentState.lastQueryParams ?? currentConfig.queryParams;

    const buildUrl = (): string => {
      // endpoint is guaranteed non-null here (early return above)
      const endpoint = currentConfig.endpoint as string;
      if (effectiveQueryParams && Object.keys(effectiveQueryParams).length > 0) {
        return `${endpoint}?${new URLSearchParams(effectiveQueryParams).toString()}`;
      }
      return endpoint;
    };

    const makeRequest = async (): Promise<Response> => {
      const latestToken = currentConfig.getToken();
      return window.fetch(buildUrl(), {
        headers: {
          ...(latestToken && { Authorization: `Bearer ${latestToken}` }),
        },
      });
    };

    try {
      let response = await makeRequest();

      // Handle 401 with token refresh if callback is provided
      if (response.status === 401 && currentConfig.onUnauthorized) {
        response = await currentConfig.onUnauthorized(makeRequest);
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: T = await response.json();
      dispatch({ type: 'FETCH_SUCCESS', data, queryParams: effectiveQueryParams });
      currentConfig.onSuccess?.(data);
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Fetch failed';
      dispatch({ type: 'FETCH_ERROR', error });
    }
  }, []); // Stable -- reads from refs

  // Auto-refresh interval: when refreshInterval > 0, mark stale on interval
  // (the domain context's useEffect will then trigger a refetch)
  useEffect(() => {
    const interval = config.refreshInterval;
    if (!interval || interval <= 0) return;

    const id = setInterval(() => {
      dispatch({ type: 'MARK_STALE' });
    }, interval);

    return () => clearInterval(id);
  }, [config.refreshInterval]);

  return { state, refetch, dispatch };
}
